import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  Application,
  User,
  OffboardingRequest,
  AccessReview,
  Role,
  Settings,
  EmailTemplate,
  AuditLog,
  Direction,
} from '../types';

// Collection references
export const collections = {
  applications: 'applications',
  users: 'users',
  offboardingRequests: 'offboardingRequests',
  accessReviews: 'accessReviews',
  roles: 'roles',
  settings: 'settings',
  emailTemplates: 'emailTemplates',
  logs: 'logs',
  notifications: 'notifications',
  directions: 'directions',
};

// Generic CRUD operations
export const firestoreAPI = {
  // Create
  async create<T>(collectionName: string, data: Partial<T>): Promise<string> {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: Timestamp.now().toDate().toISOString(),
      updatedAt: Timestamp.now().toDate().toISOString(),
    });
    return docRef.id;
  },

  // Read one
  async getById<T>(collectionName: string, id: string): Promise<T | null> {
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }
    return null;
  },

  // Read all with optional filters
  async getAll<T>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> {
    const q = query(collection(db, collectionName), ...constraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  },

  // Update
  async update<T>(
    collectionName: string,
    id: string,
    data: Partial<T>
  ): Promise<void> {
    const docRef = doc(db, collectionName, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now().toDate().toISOString(),
    });
  },

  // Delete
  async delete(collectionName: string, id: string): Promise<void> {
    const docRef = doc(db, collectionName, id);
    await deleteDoc(docRef);
  },

  // Batch write (max 500 documents)
  async batchWrite(
    collectionName: string,
    documents: any[]
  ): Promise<void> {
    const batch = writeBatch(db);
    documents.forEach((docData) => {
      const docRef = doc(collection(db, collectionName));
      batch.set(docRef, {
        ...docData,
        createdAt: Timestamp.now().toDate().toISOString(),
        updatedAt: Timestamp.now().toDate().toISOString(),
      });
    });
    await batch.commit();
  },
};

// Specific API methods for each collection

// Applications API
export const applicationsAPI = {
  getAll: () => firestoreAPI.getAll<Application>(collections.applications),
  getById: (id: string) => firestoreAPI.getById<Application>(collections.applications, id),
  create: (data: Partial<Application>) =>
    firestoreAPI.create<Application>(collections.applications, data),
  update: (id: string, data: Partial<Application>) =>
    firestoreAPI.update<Application>(collections.applications, id, data),
  delete: (id: string) => firestoreAPI.delete(collections.applications, id),
  getByAdmin: async (adminEmail: string): Promise<Application[]> => {
    return firestoreAPI.getAll<Application>(collections.applications, [
      where('admins', 'array-contains', adminEmail),
    ]);
  },
};

// Users API
export const usersAPI = {
  getAll: () => firestoreAPI.getAll<User>(collections.users),
  getById: (id: string) => firestoreAPI.getById<User>(collections.users, id),
  getByEmail: async (email: string): Promise<User | null> => {
    const users = await firestoreAPI.getAll<User>(collections.users, [
      where('email', '==', email),
      limit(1),
    ]);
    return users[0] || null;
  },
  getActive: () =>
    firestoreAPI.getAll<User>(collections.users, [where('isActive', '==', true)]),
  getActiveAndSuspended: () =>
    firestoreAPI.getAll<User>(collections.users),
  batchCreate: (users: Partial<User>[]) =>
    firestoreAPI.batchWrite(collections.users, users),
};

// Offboarding Requests API
export const offboardingAPI = {
  getAll: () =>
    firestoreAPI.getAll<OffboardingRequest>(collections.offboardingRequests, [
      orderBy('createdAt', 'desc'),
    ]),
  getById: (id: string) =>
    firestoreAPI.getById<OffboardingRequest>(collections.offboardingRequests, id),
  create: (data: Partial<OffboardingRequest>) =>
    firestoreAPI.create<OffboardingRequest>(collections.offboardingRequests, data),
  update: (id: string, data: Partial<OffboardingRequest>) =>
    firestoreAPI.update<OffboardingRequest>(collections.offboardingRequests, id, data),
  getByStatus: (status: string) =>
    firestoreAPI.getAll<OffboardingRequest>(collections.offboardingRequests, [
      where('status', '==', status),
    ]),
};

// Access Reviews API
export const reviewsAPI = {
  getAll: () =>
    firestoreAPI.getAll<AccessReview>(collections.accessReviews, [
      orderBy('createdAt', 'desc'),
    ]),
  getById: (id: string) =>
    firestoreAPI.getById<AccessReview>(collections.accessReviews, id),
  create: (data: Partial<AccessReview>) =>
    firestoreAPI.create<AccessReview>(collections.accessReviews, data),
  update: (id: string, data: Partial<AccessReview>) =>
    firestoreAPI.update<AccessReview>(collections.accessReviews, id, data),
};

// Roles API
export const rolesAPI = {
  getAll: () => firestoreAPI.getAll<Role>(collections.roles),
  getByEmail: async (email: string): Promise<Role | null> => {
    return firestoreAPI.getById<Role>(collections.roles, email);
  },
  setRole: async (email: string, role: string): Promise<void> => {
    const docRef = doc(db, collections.roles, email);
    await updateDoc(docRef, {
      email,
      role,
      assignedAt: Timestamp.now().toDate().toISOString(),
    });
  },
};

// Settings API
export const settingsAPI = {
  get: async (): Promise<Settings | null> => {
    return firestoreAPI.getById<Settings>(collections.settings, 'default');
  },
  update: async (data: Partial<Settings>): Promise<void> => {
    return firestoreAPI.update<Settings>(collections.settings, 'default', data);
  },
  createOrUpdate: async (data: Partial<Settings>): Promise<void> => {
    const docRef = doc(db, collections.settings, 'default');
    await setDoc(docRef, data, { merge: true });
  },
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: () => firestoreAPI.getAll<EmailTemplate>(collections.emailTemplates),
  getById: (id: string) =>
    firestoreAPI.getById<EmailTemplate>(collections.emailTemplates, id),
  getByType: async (type: string): Promise<EmailTemplate | null> => {
    const templates = await firestoreAPI.getAll<EmailTemplate>(
      collections.emailTemplates,
      [where('type', '==', type), limit(1)]
    );
    return templates[0] || null;
  },
  update: (id: string, data: Partial<EmailTemplate>) =>
    firestoreAPI.update<EmailTemplate>(collections.emailTemplates, id, data),
  create: (data: Partial<EmailTemplate>) =>
    firestoreAPI.create<EmailTemplate>(collections.emailTemplates, data),
};

// Audit Logs API
export const logsAPI = {
  create: (data: Partial<AuditLog>) =>
    firestoreAPI.create<AuditLog>(collections.logs, data),
  getRecent: (limitCount: number = 100) =>
    firestoreAPI.getAll<AuditLog>(collections.logs, [
      orderBy('timestamp', 'desc'),
      limit(limitCount),
    ]),
};

// Directions API
export const directionsAPI = {
  getAll: () =>
    firestoreAPI.getAll<Direction>(collections.directions, [
      orderBy('name', 'asc'),
    ]),
  getById: (id: string) =>
    firestoreAPI.getById<Direction>(collections.directions, id),
  create: (data: Partial<Direction>) =>
    firestoreAPI.create<Direction>(collections.directions, data),
  delete: (id: string) => firestoreAPI.delete(collections.directions, id),
};
